// Stanford CS234: Reinforcement Learning Course Structure
// https://web.stanford.edu/class/cs234/

export const STANFORD_RL_COURSE = {
  id: "stanford_cs234",
  name: "Stanford CS234: Reinforcement Learning",
  provider: "Stanford Online",
  url: "https://web.stanford.edu/class/cs234/",
  duration: "10 weeks",
  difficulty: "advanced",
  prerequisites: [
    "Linear Algebra & Matrix Theory",
    "Probability & Statistics",
    "Python",
    "Deep Learning Fundamentals"
  ],

  modules: [
    {
      week: 1,
      title: "Introduction to RL",
      description: "Course overview, RL problem formulation, examples of RL applications",
      skills: ["Reinforcement Learning Fundamentals"],
      project: null,
      estimatedHours: 8,
      readings: ["Sutton & Barto Ch. 1"]
    },
    {
      week: 2,
      title: "Markov Decision Processes",
      description: "MDPs, Bellman equations, value iteration, policy iteration",
      skills: ["Markov Decision Processes (MDPs)"],
      project: {
        name: "Grid World MDP Solver",
        description: "Implement value iteration and policy iteration for a simple grid world environment",
        salaryImpact: 5000,
        githubTemplate: "https://github.com/stanford-cs234/gridworld-starter"
      },
      estimatedHours: 10,
      readings: ["Sutton & Barto Ch. 3-4"]
    },
    {
      week: 3,
      title: "Model-Free Prediction",
      description: "Monte Carlo methods, Temporal Difference learning, TD(0), TD(lambda)",
      skills: ["Monte Carlo Methods", "Temporal Difference Learning"],
      project: {
        name: "TD(0) Blackjack Agent",
        description: "Build a TD learning agent to estimate state values in blackjack",
        salaryImpact: 6000,
        githubTemplate: null
      },
      estimatedHours: 10,
      readings: ["Sutton & Barto Ch. 5-6"]
    },
    {
      week: 4,
      title: "Model-Free Control",
      description: "Q-learning, SARSA, exploration vs exploitation",
      skills: ["Q-Learning & DQN"],
      project: {
        name: "Q-Learning Lunar Lander",
        description: "Train a tabular Q-learning agent to land a spacecraft in OpenAI Gym",
        salaryImpact: 8000,
        githubTemplate: null
      },
      estimatedHours: 12,
      readings: ["Sutton & Barto Ch. 6"]
    },
    {
      week: 5,
      title: "Value Function Approximation",
      description: "Function approximation, DQN, experience replay, target networks",
      skills: ["Deep Learning Fundamentals", "Q-Learning & DQN"],
      project: {
        name: "Atari DQN",
        description: "Implement DQN with experience replay and target networks on Atari Breakout",
        salaryImpact: 10000,
        githubTemplate: null
      },
      estimatedHours: 15,
      readings: ["DQN Paper (Mnih et al. 2015)"]
    },
    {
      week: 6,
      title: "Policy Gradient Methods",
      description: "REINFORCE, variance reduction, baselines",
      skills: ["Policy Gradient Methods"],
      project: {
        name: "REINFORCE CartPole",
        description: "Implement vanilla policy gradient with baseline on CartPole",
        salaryImpact: 8000,
        githubTemplate: null
      },
      estimatedHours: 12,
      readings: ["Policy Gradient Methods Paper (Sutton et al. 1999)"]
    },
    {
      week: 7,
      title: "Actor-Critic Methods",
      description: "A2C, A3C, GAE, PPO fundamentals",
      skills: ["Actor-Critic Methods"],
      project: {
        name: "PPO Continuous Control",
        description: "Build PPO agent for MuJoCo continuous control tasks (HalfCheetah, Hopper)",
        salaryImpact: 12000,
        githubTemplate: null
      },
      estimatedHours: 15,
      readings: ["PPO Paper (Schulman et al. 2017)"]
    },
    {
      week: 8,
      title: "Model-Based RL",
      description: "World models, Dyna, planning with learned models",
      skills: ["Model-Based RL"],
      project: {
        name: "Dreamer Implementation",
        description: "Implement world model learning for sample-efficient RL in visual domains",
        salaryImpact: 15000,
        githubTemplate: null
      },
      estimatedHours: 18,
      readings: ["World Models Paper (Ha & Schmidhuber 2018)"]
    },
    {
      week: 9,
      title: "Exploration & Multi-Agent",
      description: "Intrinsic motivation, curiosity-driven exploration, multi-agent settings",
      skills: ["Multi-Agent RL"],
      project: {
        name: "Curiosity-Driven Exploration",
        description: "Implement ICM (Intrinsic Curiosity Module) for sparse reward environments",
        salaryImpact: 10000,
        githubTemplate: null
      },
      estimatedHours: 15,
      readings: ["Curiosity Paper (Pathak et al. 2017)"]
    },
    {
      week: 10,
      title: "RLHF & Advanced Topics",
      description: "Learning from human feedback, reward modeling, preference learning, AI alignment",
      skills: ["RLHF (Reinforcement Learning from Human Feedback)", "Reward Modeling"],
      project: {
        name: "Mini RLHF Pipeline",
        description: "Build an RLHF pipeline: collect preferences, train reward model, fine-tune with PPO",
        salaryImpact: 20000,
        githubTemplate: null
      },
      estimatedHours: 20,
      readings: ["InstructGPT Paper (Ouyang et al. 2022)"]
    }
  ],

  // Skills gained from completing the course
  totalSkillsGained: [
    "Markov Decision Processes (MDPs)",
    "Q-Learning & DQN",
    "Policy Gradient Methods",
    "Actor-Critic Methods",
    "Model-Based RL",
    "Multi-Agent RL",
    "RLHF (Reinforcement Learning from Human Feedback)"
  ],

  // Total salary impact from all projects
  totalSalaryImpact: 94000,

  // How relevant this course is for each lab
  labRelevance: {
    openai: 90,
    anthropic: 95,
    deepmind: 98,
    metaFair: 75
  },

  // Total estimated hours
  totalHours: 125
};

// Helper to get completed modules from user progress
export const getCompletedModules = (userProgress) => {
  if (!userProgress?.courses?.[STANFORD_RL_COURSE.id]) {
    return [];
  }
  return userProgress.courses[STANFORD_RL_COURSE.id].completedWeeks || [];
};

// Calculate progress stats
export const getCourseProgress = (userProgress) => {
  const completedWeeks = getCompletedModules(userProgress);
  const completedModules = STANFORD_RL_COURSE.modules.filter(m =>
    completedWeeks.includes(m.week)
  );

  const salaryGained = completedModules.reduce(
    (sum, m) => sum + (m.project?.salaryImpact || 0),
    0
  );

  const hoursCompleted = completedModules.reduce(
    (sum, m) => sum + m.estimatedHours,
    0
  );

  const skillsGained = [...new Set(completedModules.flatMap(m => m.skills))];

  return {
    completedCount: completedModules.length,
    totalCount: STANFORD_RL_COURSE.modules.length,
    progressPercent: (completedModules.length / STANFORD_RL_COURSE.modules.length) * 100,
    salaryGained,
    potentialSalary: STANFORD_RL_COURSE.totalSalaryImpact,
    remainingSalary: STANFORD_RL_COURSE.totalSalaryImpact - salaryGained,
    hoursCompleted,
    hoursRemaining: STANFORD_RL_COURSE.totalHours - hoursCompleted,
    skillsGained,
    nextModule: STANFORD_RL_COURSE.modules.find(m => !completedWeeks.includes(m.week))
  };
};

// All available learning paths (can add more courses later)
export const LEARNING_PATHS = [
  STANFORD_RL_COURSE
];
